import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { UserPlus, X, Info, CheckCircle2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Trans, useTranslation } from "react-i18next";
import Modal from "components/common/Modal";
import Combobox from "v2/views/components/common/Combobox";
import DatePicker from "components/common/DatePicker";
import { getPositionsForSport, positionToApi } from "v2/constants/positions";

const AddPlayerModal = ({ show, onClose, onCreate, teamName, teamSport }) => {
  const { t } = useTranslation();
  const positionSuggestions = useMemo(
    () => getPositionsForSport(teamSport).map((p) => p.label),
    [teamSport]
  );
  const [playerName, setPlayerName] = useState("");
  const [position, setPosition] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [createMore, setCreateMore] = useState(false);
  const [successName, setSuccessName] = useState(null);
  const nameInputRef = useRef(null);
  const successTimeoutRef = useRef(null);

  const focusNameInput = useCallback(() => {
    setTimeout(() => nameInputRef.current?.focus(), 50);
  }, []);

  // Auto-focus name input when modal opens
  useEffect(() => {
    if (show) focusNameInput();
  }, [show, focusNameInput]);

  const handleClose = () => {
    setPlayerName("");
    setPosition("");
    setDateOfBirth(null);
    setSuccessName(null);
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    onClose();
  };

  const resetForm = () => {
    setPlayerName("");
    setPosition("");
    setDateOfBirth(null);
  };

  const handleCreate = async () => {
    if (!playerName.trim()) return;
    const addedName = playerName.trim();
    try {
      await onCreate?.(
        {
          name: addedName,
          position: positionToApi(position.trim(), teamSport) || null,
          dateOfBirth,
        },
        { createMore }
      );
      if (createMore) {
        setSuccessName(addedName);
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(
          () => setSuccessName(null),
          2500
        );
        resetForm();
        focusNameInput();
      } else {
        handleClose();
      }
    } catch {
      // Parent handles error toast
    }
  };

  const isValid = playerName.trim().length > 0;

  return (
    <Modal show={show} onClose={handleClose} maxWidth="480px">
      {/* Header */}
      <div
        className="relative overflow-hidden p-7"
        style={{
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        {/* Decorative circles */}
        <div
          className="absolute rounded-full"
          style={{
            width: 120,
            height: 120,
            top: -30,
            right: -30,
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 80,
            height: 80,
            bottom: -40,
            right: 60,
            background: "rgba(255, 255, 255, 0.08)",
          }}
        />

        {/* Header content */}
        <div className="relative flex items-start justify-between">
          <div>
            {/* Icon */}
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <UserPlus size={24} className="text-white" />
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-white">
              {t("workspace.addPlayerModal.title")}
            </h2>
            <p
              className="mt-1.5 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              <Trans
                i18nKey="workspace.addPlayerModal.subtitle"
                values={{
                  name: teamName || t("workspace.addPlayerModal.yourTeam"),
                }}
                components={{ bold: <span className="font-medium" /> }}
              />
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
            style={{ background: "rgba(255, 255, 255, 0.2)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Body */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (isValid) handleCreate();
        }}
      >
        <div className="p-7">
          {/* Success banner */}
          <AnimatePresence>
            {successName && (
              <motion.div
                initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                animate={{ opacity: 1, height: "auto", marginBottom: 20 }}
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="flex items-center gap-2.5 rounded-xl border border-[#DCFCE7] bg-[#F0FDF4] px-4 py-3">
                  <CheckCircle2
                    size={16}
                    className="flex-shrink-0 text-[#10B981]"
                  />
                  <span className="text-sm text-[#374151]">
                    <Trans
                      i18nKey="workspace.addPlayerModal.successAdded"
                      values={{ name: successName }}
                      components={{ bold: <span className="font-medium" /> }}
                    />
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Player Name Field */}
          <div className="mb-5">
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              {t("workspace.addPlayerModal.nameLabel")}
            </label>
            <input
              ref={nameInputRef}
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder={t("workspace.addPlayerModal.namePlaceholder")}
              className="w-full rounded-[10px] border border-[#E5E7EB] px-4 py-3 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150"
              style={{
                boxShadow: "none",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#E07A3A";
                e.target.style.boxShadow = "0 0 0 3px rgba(224, 122, 58, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#E5E7EB";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Date of Birth Field */}
          <div className="mb-5">
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              <Trans
                i18nKey="workspace.addPlayerModal.dobLabel"
                components={{
                  muted: <span className="font-normal text-[#9CA3AF]" />,
                }}
              />
            </label>
            <DatePicker
              value={dateOfBirth}
              onChange={setDateOfBirth}
              placeholder={t("workspace.addPlayerModal.dobPlaceholder")}
              maxDate={new Date()}
            />
          </div>

          {/* Position Selector */}
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              <Trans
                i18nKey="workspace.addPlayerModal.positionLabel"
                components={{
                  muted: <span className="font-normal text-[#9CA3AF]" />,
                }}
              />
            </label>
            <Combobox
              suggestions={positionSuggestions}
              value={position}
              onChange={setPosition}
              placeholder={t("workspace.addPlayerModal.positionPlaceholder")}
            />
          </div>

          {/* Info Box */}
          <div
            className="flex gap-3 rounded-xl p-4"
            style={{
              background:
                "linear-gradient(135deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 100%)",
              border: "1px solid rgba(232, 133, 74, 0.15)",
            }}
          >
            <Info size={20} className="mt-0.5 flex-shrink-0 text-[#E07A3A]" />
            <p className="text-[13px] leading-relaxed text-[#6B7280]">
              {t("workspace.addPlayerModal.info")}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-[#F3F4F6] px-7 py-5">
          <label className="flex cursor-pointer select-none items-center gap-2">
            <button
              type="button"
              role="switch"
              aria-checked={createMore}
              onClick={() => setCreateMore(!createMore)}
              className="relative h-5 w-9 rounded-full transition-colors duration-150"
              style={{
                background: createMore ? "#E07A3A" : "#E5E7EB",
              }}
            >
              <span
                className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform duration-150"
                style={{
                  transform: createMore ? "translateX(16px)" : "translateX(0)",
                }}
              />
            </button>
            <span className="text-sm text-[#6B7280]">
              {t("workspace.addPlayerModal.createMore")}
            </span>
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
            >
              {t("common.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={!isValid}
              className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
              style={
                isValid
                  ? {
                      background:
                        "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                      boxShadow: "0 2px 8px rgba(224, 122, 58, 0.3)",
                      cursor: "pointer",
                    }
                  : {
                      background: "#F3F4F6",
                      color: "#9CA3AF",
                      cursor: "not-allowed",
                    }
              }
            >
              {t("workspace.addPlayerModal.submit")}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AddPlayerModal;
